import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useContractTypeService from '../../../app/services/hooks/useContractTypeService';
import useHandleErrorFields from '../../../hooks/useHandleErrorFields';
import InputField from '../../../components/InputField';
import TextAreaField from '../../../components/TextAreaField';
import OptionSelector from '../../../components/OptionSelector';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';

const ContractTypeForm: React.FC = () => {
    const { id } = useParams<{ id?: string }>();
    const history = useHistory();
    const isEdit = Boolean(id);
    const { storeContractType, editContractType, updateContractType, fetchingStore, fetchingEdit, fetchingUpdate } = useContractTypeService();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    
    const [form, setForm] = useState({
        name: '',
        description: '',
        icon: '',
        price: '',
        has_articles: false,
        signatures_required: 0,
        is_signature_only: false
    });

    useEffect(() => {
        if (isEdit && id) {
            loadContractType();
        }
    }, [isEdit, id]);

    const loadContractType = () => {
        if (!id) return;
        editContractType(parseInt(id), {
            onSuccess: (res: any) => {
                const data = res.data.contract_type;
                setForm({
                    name: data.name || '',
                    description: data.description || '',
                    icon: data.icon || '',
                    price: data.price || '',
                    has_articles: data.has_articles || false,
                    signatures_required: data.signatures_required || 0,
                    is_signature_only: data.is_signature_only || false
                });
            },
            onError: (res: any) => {
                toast.error(res.message || 'Error al cargar');
                history.push('/intranet/contract-types');
            }
        });
    };

    const handleInputChange = (field: string, value: any) => {
        setForm({ ...form, [field]: value });
    };

    const handleSubmit = () => {
        setErrors({});
        
        if (isEdit && id) {
            updateContractType(parseInt(id), form, {
                onSuccess: (res: any) => {
                    toast.success(res.message || 'Tipo de contrato actualizado exitosamente');
                    setErrors({});
                    history.push('/intranet/contract-types');
                },
                onError: (res: any) => {
                    toast.error(res.message || 'Error al actualizar');
                },
                onFieldError: (res: any) => {
                    setErrors(res.data);
                    toast.error('Por favor corrige los errores');
                }
            });
        } else {
            storeContractType(form, {
                onSuccess: (res: any) => {
                    toast.success(res.message || 'Tipo de contrato creado exitosamente');
                    setErrors({});
                    history.push('/intranet/contract-types');
                },
                onError: (res: any) => {
                    toast.error(res.message || 'Error al crear');
                },
                onFieldError: (res: any) => {
                    setErrors(res.data);
                    toast.error('Por favor corrige los errores');
                }
            });
        }
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            <h4 className="mb-4">{isEdit ? 'Editar' : 'Nuevo'} Tipo de Contrato</h4>
                            
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <InputField
                                        label="Nombre"
                                        value={form.name}
                                        onChange={(value) => handleInputChange('name', value)}
                                        onFocus={() => onFocusRemove('name')}
                                        error={fieldErrorMessage('name')}
                                        required
                                    />
                                </div>
                                <div className="col-md-3 mb-3">
                                    <InputField
                                        label="Precio (CLP)"
                                        type="number"
                                        value={form.price}
                                        onChange={(value) => handleInputChange('price', value)}
                                        onFocus={() => onFocusRemove('price')}
                                        error={fieldErrorMessage('price')}
                                        required
                                    />
                                </div>
                                <div className="col-md-3 mb-3">
                                    <InputField
                                        label="Ícono (emoji)"
                                        value={form.icon}
                                        onChange={(value) => handleInputChange('icon', value)}
                                        onFocus={() => onFocusRemove('icon')}
                                        error={fieldErrorMessage('icon')}
                                        placeholder="📄"
                                    />
                                </div>
                                <div className="col-md-12 mb-3">
                                    <TextAreaField
                                        label="Descripción"
                                        value={form.description}
                                        onChange={(value) => handleInputChange('description', value)}
                                        onFocus={() => onFocusRemove('description')}
                                        error={fieldErrorMessage('description')}
                                        rows={3}
                                    />
                                </div>
                                <div className="col-md-12 mb-3">
                                    <div className="mb-3">
                                        <label className="form-label fw-bold mb-1">¿Es un documento solo para firmas?</label>
                                        <p className="text-muted small mb-0" style={{ lineHeight: '1.4' }}>
                                            Para documentos que ya vienen redactados y solo necesitan recolectar firmas.
                                            Se sube como plantilla final en la pestaña Templates, no tiene costo, y solo los
                                            administradores pueden usarlo.
                                        </p>
                                    </div>
                                    <OptionSelector
                                        name="is_signature_only"
                                        options={[
                                            { value: 'false', label: 'No, es un contrato normal', description: 'El cliente llena un formulario, ve el preview y paga' },
                                            { value: 'true', label: 'Sí, solo para firmas', description: 'Salta formulario, preview y pago: pasa directo a agregar firmantes' }
                                        ]}
                                        selectedValue={form.is_signature_only ? 'true' : 'false'}
                                        onChange={(value) => handleInputChange('is_signature_only', value === 'true')}
                                    />
                                </div>
                                <div className="col-md-12 mb-3">
                                    <div className="mb-3">
                                        <label className="form-label fw-bold mb-1">¿Incluye artículos opcionales?</label>
                                        <p className="text-muted small mb-0" style={{ lineHeight: '1.4' }}>
                                            Los artículos opcionales son cláusulas que el cliente puede seleccionar al crear el contrato
                                        </p>
                                    </div>
                                    <OptionSelector
                                        name="has_articles"
                                        options={[
                                            { value: 'true', label: 'Sí, incluye artículos opcionales', description: 'El cliente podrá seleccionar artículos adicionales' },
                                            { value: 'false', label: 'No, solo campos de formulario', description: 'Solo se mostrarán los campos de entrada' }
                                        ]}
                                        selectedValue={form.has_articles ? 'true' : 'false'}
                                        onChange={(value) => handleInputChange('has_articles', value === 'true')}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <InputField
                                        label="Firmas requeridas"
                                        type="number"
                                        value={String(form.signatures_required)}
                                        onChange={(value) => handleInputChange('signatures_required', parseInt(value) || 0)}
                                        onFocus={() => onFocusRemove('signatures_required')}
                                        error={fieldErrorMessage('signatures_required')}
                                        placeholder="0"
                                    />
                                    <small className="text-muted">
                                        Número de personas que deben firmar este contrato. 0 = no requiere firmas.
                                    </small>
                                </div>
                            </div>

                            <div className="d-flex justify-content-between mt-4">
                                <ButtonCancelForm callbackCancel={() => history.push('/intranet/contract-types')} />
                                <ButtonSaveForm
                                    callbackSave={handleSubmit}
                                    locked={fetchingStore || fetchingUpdate}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContractTypeForm;
